import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, LanguageSwitch } from '../components/shared';
import { api } from '../lib/api';
import { getApiErrorMessage } from '../lib/errors';

/**
 * `/reset-password?token=…` — final step of the password-reset flow. Asks
 * for a new password (twice) and submits it together with the token from
 * the email link. On success, redirects to /login.
 */
export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-ivory flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <LanguageSwitch className="justify-end -mt-2 -mr-2 mb-2" />
          <div className="text-4xl mb-2">🐾</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.resetPassword.missingToken.heading')}</h1>
          <p className="text-sm text-gray-700 mt-3">
            {t('auth.resetPassword.missingToken.body')}
          </p>
          <div className="mt-6">
            <Link to="/forgot-password">
              <Button variant="primary" fullWidth>{t('auth.resetPassword.missingToken.requestNewLink')}</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: typeof validationErrors = {};
    if (!password) errors.password = t('auth.resetPassword.passwordRequired');
    else if (password.length < 8) errors.password = t('auth.resetPassword.passwordTooShort');
    if (!confirmPassword) errors.confirmPassword = t('auth.resetPassword.confirmPasswordRequired');
    else if (password !== confirmPassword) errors.confirmPassword = t('auth.resetPassword.passwordsMismatch');

    setValidationErrors(errors);
    if (Object.keys(errors).length) return;

    setErrorMessage(null);
    setSubmitting(true);
    try {
      const res = await api.resetPassword(token, password);
      setSuccessMessage(res.message);
      // Brief delay so the success message is visible before redirecting.
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: unknown) {
      setErrorMessage(
        getApiErrorMessage(err, t('auth.resetPassword.resetFailed')),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-ivory flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <LanguageSwitch className="justify-end -mt-2 -mr-2 mb-2" />
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🐾</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.resetPassword.heading')}</h1>
          <p className="text-sm text-gray-600 mt-2">
            {t('auth.resetPassword.intro')}
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-sage-50 border border-sage-200 rounded-lg">
            <p className="text-sm text-sage-800 font-medium">{successMessage}</p>
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium">{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label={t('auth.resetPassword.passwordLabel')}
            name="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (validationErrors.password) setValidationErrors({ ...validationErrors, password: undefined });
            }}
            error={validationErrors.password}
            placeholder={t('auth.resetPassword.passwordPlaceholder')}
            required
            autoComplete="new-password"
          />
          <Input
            label={t('auth.resetPassword.confirmPasswordLabel')}
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (validationErrors.confirmPassword) setValidationErrors({ ...validationErrors, confirmPassword: undefined });
            }}
            error={validationErrors.confirmPassword}
            placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
            required
            autoComplete="new-password"
          />

          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={submitting}
            disabled={submitting || !!successMessage}
          >
            {t('auth.resetPassword.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-sage-600 font-medium hover:text-sage-700 transition-colors"
          >
            {t('auth.resetPassword.backToLogin')}
          </Link>
        </div>
      </Card>
    </div>
  );
};
